import { App, Form } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@stores/auth.store';
import { persistSchoolTimeConfiguration, createCampusBranchId } from '@services/registry';
import { useCampusBranches } from '@hooks/tenant/useCampusBranches';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';

export const SCHOOL_TIME_SCHEDULE_DAYS = [
    { key: 'mon', label: 'Monday' },
    { key: 'tue', label: 'Tuesday' },
    { key: 'wed', label: 'Wednesday' },
    { key: 'thu', label: 'Thursday' },
    { key: 'fri', label: 'Friday' },
    { key: 'sat', label: 'Saturday' },
    { key: 'sun', label: 'Sunday' },
];

function normalizeTimeValue(v) {
    if (!v) return null;
    if (typeof v === 'string') return v;
    if (dayjs.isDayjs(v)) return v.format('HH:mm');
    return null;
}

function toDisplayTime(value) {
    const t = normalizeTimeValue(value);
    if (!t) return null;
    const d = dayjs(t, 'HH:mm', true);
    return d.isValid() ? d.format('hh:mm A') : null;
}

function formatRange({ from, to }) {
    const f = toDisplayTime(from);
    const t = toDisplayTime(to);
    if (!f || !t) return null;
    return `${f} - ${t}`;
}

function computeBranchSummary(branch) {
    if (!branch?.schedule) return '—';
    const active = [];
    for (const d of SCHOOL_TIME_SCHEDULE_DAYS) {
        const cur = branch.schedule?.[d.key] || {};
        if (!cur.on) continue;
        const range = formatRange({ from: cur.from, to: cur.to });
        if (range) active.push({ from: range.split(' - ')[0], to: range.split(' - ')[1] });
    }
    if (!active.length) return 'No active days';
    const starts = active.map((a) => a.from).sort();
    const ends = active.map((a) => a.to).sort();
    return `${starts[0]} - ${ends[ends.length - 1]}`;
}

function buildCleanBranches(nextBranches) {
    return (nextBranches || []).map((b, idx) => {
        const schedule = {};
        for (const d of SCHOOL_TIME_SCHEDULE_DAYS) {
            const cur = b?.schedule?.[d.key] || {};
            schedule[d.key] = {
                on: !!cur.on,
                from: cur.on ? (normalizeTimeValue(cur.from) || null) : null,
                to: cur.on ? (normalizeTimeValue(cur.to) || null) : null,
            };
        }
        return {
            id: b?.id || `branch_${idx + 1}`,
            branchName: (b?.branchName || '').trim(),
            address: (b?.address || '').trim(),
            effectiveFrom: b?.effectiveFrom || null,
            schedule,
        };
    });
}

function createEmptyBranch(tenantId) {
    const schedule = {};
    for (const d of SCHOOL_TIME_SCHEDULE_DAYS) {
        schedule[d.key] = { on: false, from: null, to: null };
    }
    return {
        id: createCampusBranchId(tenantId),
        branchName: '',
        address: '',
        effectiveFrom: null,
        schedule,
    };
}

function validateBranchScheduleComplete(branch) {
    const name = (branch?.branchName || '').trim();
    if (!name) {
        return { ok: false, message: 'Branch name is required' };
    }
    const anyOn = SCHOOL_TIME_SCHEDULE_DAYS.some((d) => branch?.schedule?.[d.key]?.on);
    if (!anyOn) {
        return { ok: false, message: `Select at least one working day for "${name}"` };
    }
    for (const d of SCHOOL_TIME_SCHEDULE_DAYS) {
        const cur = branch?.schedule?.[d.key];
        if (!cur?.on) continue;
        if (!cur.from || !cur.to) {
            return { ok: false, message: `Set From and To times for ${d.label} on "${name}"` };
        }
    }
    return { ok: true };
}

export function toDayjsTimeValue(v) {
    if (!v) return null;
    if (dayjs.isDayjs(v)) return v;
    const d = dayjs(v, 'HH:mm', true);
    return d.isValid() ? d : null;
}

/**
 * School time / campus schedule UI state, validation, and persistence.
 */
export function useSchoolTimeConfig() {
    const { message } = App.useApp();
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const patchUser = useAuthStore((s) => s.patchUser);
    const { branches: remoteBranches, remoteEtag } = useCampusBranches({ tenantId });
    const { dateFormat, dateInputFormats } = useTenantDateFormat();
    const [branchForm] = Form.useForm();
    const [effectiveForm] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [effectiveOpen, setEffectiveOpen] = useState(false);
    const [effectiveBranchId, setEffectiveBranchId] = useState(null);
    const [draftBranches, setDraftBranches] = useState([]);
    const [dirtyIds, setDirtyIds] = useState(() => new Set());

    useEffect(() => {
        // Normalize + deep-clone schedule shape from remote snapshots so each
        // day is an isolated object and cannot accidentally mirror another day.
        setDraftBranches(buildCleanBranches(remoteBranches));
        setDirtyIds(new Set());
    }, [remoteEtag, remoteBranches]);

    const getBranchSummary = useCallback((branch) => computeBranchSummary(branch), []);

    const updateBranchSchedule = useCallback(({ branchId, dayKey, patch }) => {
        setDraftBranches((prev) =>
            (Array.isArray(prev) ? prev : []).map((b) => {
                if (b.id !== branchId) return b;
                const current = b?.schedule?.[dayKey] || { on: false, from: null, to: null };
                const nextDay = {
                    on: typeof patch?.on === 'boolean' ? patch.on : !!current.on,
                    from: patch?.from !== undefined ? patch.from : current.from ?? null,
                    to: patch?.to !== undefined ? patch.to : current.to ?? null,
                };
                const nextSchedule = { ...(b.schedule || {}), [dayKey]: nextDay };
                return { ...b, schedule: nextSchedule };
            })
        );
        setDirtyIds((prev) => {
            const s = new Set(prev);
            s.add(branchId);
            return s;
        });
    }, []);

    const persistSnapshot = useCallback(
        async (nextBranches) => {
            if (!tenantId) return false;
            const cleanBranches = buildCleanBranches(nextBranches);
            setSaving(true);
            try {
                const { attendanceConfig } = await persistSchoolTimeConfiguration({
                    tenantId,
                    cleanBranches,
                });
                patchUser({ attendanceConfig });
                return true;
            } catch (e) {
                message.error(e?.message || 'Failed to save configuration');
                return false;
            } finally {
                setSaving(false);
            }
        },
        [tenantId, message, patchUser]
    );

    const persistBranches = useCallback(
        async (nextBranches, onlyValidateBranchId) => {
            const cleanBranches = buildCleanBranches(nextBranches);

            if (!cleanBranches.length || !cleanBranches.some((b) => b.branchName)) {
                message.error('Add at least one branch');
                return false;
            }

            for (const b of cleanBranches) {
                if (!b.branchName) continue;
                if (onlyValidateBranchId && b.id !== onlyValidateBranchId) continue;

                if (!b.effectiveFrom) {
                    message.error(`Select implement date for "${b.branchName}"`);
                    return false;
                }
                const anyOn = SCHOOL_TIME_SCHEDULE_DAYS.some((d) => b.schedule?.[d.key]?.on);
                if (!anyOn) {
                    message.error(`Select at least one working day for "${b.branchName}"`);
                    return false;
                }
                for (const d of SCHOOL_TIME_SCHEDULE_DAYS) {
                    const cur = b.schedule?.[d.key];
                    if (!cur?.on) continue;
                    if (!cur.from || !cur.to) {
                        message.error(`Set From/To time for ${d.label} in "${b.branchName}"`);
                        return false;
                    }
                }
            }

            if (!tenantId) return false;
            setSaving(true);
            try {
                const { attendanceConfig } = await persistSchoolTimeConfiguration({
                    tenantId,
                    cleanBranches,
                });
                patchUser({ attendanceConfig });
                return true;
            } catch (e) {
                message.error(e?.message || 'Failed to save configuration');
                return false;
            } finally {
                setSaving(false);
            }
        },
        [tenantId, message, patchUser]
    );

    const openAdd = useCallback(() => {
        branchForm.resetFields();
        setAddOpen(true);
    }, [branchForm]);

    const submitAddBranch = useCallback(async () => {
        if (!tenantId) return;
        try {
            const v = await branchForm.validateFields();
            const branch = createEmptyBranch(tenantId);
            branch.branchName = (v.branchName || '').trim();
            branch.address = (v.address || '').trim();
            if (!branch.branchName) {
                message.error('Branch name is required');
                return;
            }
            const next = [...draftBranches, branch];
            const ok = await persistSnapshot(next);
            if (ok) {
                setDraftBranches(next);
                message.success('Campus saved. Set working days and times on the card, then Save.');
                setAddOpen(false);
            }
        } catch (e) {
            if (e?.message === 'Validation failed') return;
            message.error(e?.message || 'Failed to add branch');
        }
    }, [tenantId, draftBranches, branchForm, message, persistSnapshot]);

    const removeBranch = useCallback(
        async (id) => {
            if (!tenantId) return;
            const next = draftBranches.filter((b) => b.id !== id);
            const ok = await persistSnapshot(next);
            if (ok) {
                message.success('Branch removed');
                setDraftBranches(next);
                setDirtyIds((prev) => {
                    const s = new Set(prev);
                    s.delete(id);
                    return s;
                });
            }
        },
        [tenantId, draftBranches, persistSnapshot, message]
    );

    const saveBranch = useCallback(
        (id) => {
            if (!tenantId) return;
            const b = draftBranches.find((x) => x.id === id);
            if (!b) return;
            const scheduleCheck = validateBranchScheduleComplete(b);
            if (!scheduleCheck.ok) {
                message.error(scheduleCheck.message);
                return;
            }
            setEffectiveBranchId(id);
            effectiveForm.setFieldsValue({
                effectiveFrom: b?.effectiveFrom ? dayjs(b.effectiveFrom) : dayjs(),
            });
            setEffectiveOpen(true);
        },
        [tenantId, draftBranches, effectiveForm, message]
    );

    const confirmEffectiveSave = useCallback(async () => {
        if (!tenantId || !effectiveBranchId) return;
        try {
            const v = await effectiveForm.validateFields();
            const d = v?.effectiveFrom;
            const effectiveFrom = d ? dayjs(d).format('YYYY-MM-DD') : null;
            const next = draftBranches.map((b) => (b.id === effectiveBranchId ? { ...b, effectiveFrom } : b));
            const ok = await persistBranches(next, effectiveBranchId);
            if (ok) {
                message.success('Branch saved');
                setDraftBranches(next);
                setDirtyIds((prev) => {
                    const s = new Set(prev);
                    s.delete(effectiveBranchId);
                    return s;
                });
                setEffectiveOpen(false);
                setEffectiveBranchId(null);
            }
        } catch (e) {
            if (e?.message === 'Validation failed') return;
            message.error(e?.message || 'Failed to save');
        }
    }, [tenantId, effectiveBranchId, effectiveForm, draftBranches, persistBranches, message]);

    const cancelEdit = useCallback((branchId) => {
        setDraftBranches(buildCleanBranches(remoteBranches));
        setDirtyIds((prev) => {
            const s = new Set(prev);
            s.delete(branchId);
            return s;
        });
    }, [remoteBranches]);

    const closeAddModal = useCallback(() => setAddOpen(false), []);
    const closeEffectiveModal = useCallback(() => {
        setEffectiveOpen(false);
        setEffectiveBranchId(null);
    }, []);

    return {
        scheduleDays: SCHOOL_TIME_SCHEDULE_DAYS,
        branchForm,
        effectiveForm,
        draftBranches,
        dirtyIds,
        saving,
        addOpen,
        effectiveOpen,
        openAdd,
        closeAddModal,
        closeEffectiveModal,
        submitAddBranch,
        removeBranch,
        saveBranch,
        confirmEffectiveSave,
        cancelEdit,
        updateBranchSchedule,
        getBranchSummary,
        toDayjsTime: toDayjsTimeValue,
        dateFormat,
        dateInputFormats,
    };
}
